<script setup lang="ts">
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Member, defaultUserInfo } from '@/types/Member'
import { toast } from '@/components/ui/toast'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { h, onMounted, Ref, inject, ref, computed, watch, nextTick } from 'vue'
import * as z from 'zod'
import { useFaceIDAuthentication, updateUser } from '@/services/User'
import { NewSparkData, getData } from '@/services/idb'
import FaceIDAuth from "@/components/FaceIDAuth.vue"
import { Upload, Users, HardDrive, Activity, TrendingUp, TrendingDown, Camera, Eye, EyeOff } from 'lucide-vue-next'
import { Switch } from '@/components/ui/switch'
import vhostService from '@/services/Vhost'
import * as echarts from 'echarts/core'
import { BarChart, LineChart, PieChart, GaugeChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

echarts.use([BarChart, LineChart, PieChart, GaugeChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

const faceID = ref(false)
const userInfo = inject<Ref<Member> | null>('userInfo', null)
const updatedInfo = ref<Member>(defaultUserInfo)
const storedData = ref<NewSparkData | null>(null)
let removeFaceID: () => Promise<boolean> = async () => false
let registerFaceID: () => Promise<boolean> = async () => false

if (userInfo?.value) {
	updatedInfo.value = userInfo.value
}

const toggleFaceID = async (val: boolean): Promise<void> => {
	faceID.value = !val
	if (faceID.value) {
		const success = await registerFaceID()
		faceID.value = success
	} else {
		await removeFaceID()
	}
}

const profileFormSchema = toTypedSchema(z.object({
	username: z.string().min(2).max(30),
	email: z.string().email(),
	bio: z.string().max(160).min(4),
}))

const { handleSubmit, resetForm } = useForm({
	validationSchema: profileFormSchema,
	initialValues: { bio: '' },
})

const onSubmit = handleSubmit((values) => {
	toast({
		title: 'Profile updated',
		description: h('pre', { class: 'mt-2 w-[340px] rounded-md bg-slate-950 p-4' }, h('code', { class: 'text-white' }, JSON.stringify(values, null, 2))),
	})
})

// ─── Charts ───────────────────────────────────────────
const activityChartRef = ref<HTMLElement | null>(null)
const storageChartRef = ref<HTMLElement | null>(null)
const uploadsChartRef = ref<HTMLElement | null>(null)

function getCssColor(varName: string, fallback: string): string {
	const val = getComputedStyle(document.documentElement).getPropertyValue(varName)?.trim()
	if (!val || !/^\d+/.test(val)) return fallback
	// Canvas API needs comma-separated: "hsl(204, 77%, 47%)" not space-separated
	const parts = val.split(/\s+/)
	return parts.length >= 3 ? `hsl(${parts[0]}, ${parts[1]}, ${parts[2]})` : `hsl(${val})`
}

function withAlpha(hslColor: string, alpha: number): string {
	return hslColor.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`)
}

const initCharts = () => {
	const primary = getCssColor('--primary', '#3b82f6')
	const muted = getCssColor('--muted', '#f1f5f9')
	const mutedFg = getCssColor('--muted-foreground', '#64748b')
	const fg = getCssColor('--foreground', '#0f172a')

	// Activity sparkline
	if (activityChartRef.value) {
		const chart = echarts.init(activityChartRef.value)
		const days = Array.from({ length: 14 }, (_, i) => {
			const d = new Date(); d.setDate(d.getDate() - 13 + i)
			return d.toLocaleDateString('en', { month: 'short', day: 'numeric' })
		})
		const values = [12, 19, 8, 25, 18, 32, 28, 35, 22, 40, 30, 45, 38, 42]
		chart.setOption({
			tooltip: { trigger: 'axis', backgroundColor: 'rgba(0,0,0,0.8)', borderWidth: 0, textStyle: { color: '#fff', fontSize: 11 } },
			grid: { top: 8, right: 8, bottom: 24, left: 32 },
			xAxis: { type: 'category', data: days, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { fontSize: 9, color: mutedFg, interval: 2 } },
			yAxis: { type: 'value', splitLine: { lineStyle: { color: muted, type: 'dashed' } }, axisLabel: { fontSize: 9, color: mutedFg } },
			series: [{
				type: 'line', data: values, smooth: true, symbol: 'none',
				lineStyle: { width: 2, color: primary },
				areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
					{ offset: 0, color: withAlpha(primary, 0.2) },
					{ offset: 1, color: 'transparent' }
				]) }
			}]
		})
		window.addEventListener('resize', () => chart.resize())
	}

	// Storage donut
	if (storageChartRef.value) {
		const chart = echarts.init(storageChartRef.value)
		chart.setOption({
			tooltip: { trigger: 'item', backgroundColor: 'rgba(0,0,0,0.8)', borderWidth: 0, textStyle: { color: '#fff', fontSize: 11 } },
			series: [{
				type: 'pie', radius: ['60%', '80%'], center: ['50%', '50%'],
				avoidLabelOverlap: false,
				label: { show: true, position: 'center', formatter: '85%', fontSize: 20, fontWeight: 'bold', color: fg },
				emphasis: { label: { show: true, fontSize: 22, fontWeight: 'bold' } },
				labelLine: { show: false },
				itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
				data: [
					{ value: 85, name: 'Used', itemStyle: { color: primary } },
					{ value: 15, name: 'Free', itemStyle: { color: muted } }
				]
			}]
		})
		window.addEventListener('resize', () => chart.resize())
	}

	// Uploads bar
	if (uploadsChartRef.value) {
		const chart = echarts.init(uploadsChartRef.value)
		const months = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
		const values = [320, 280, 420, 380, 510, 190, 140]
		chart.setOption({
			tooltip: { trigger: 'axis', backgroundColor: 'rgba(0,0,0,0.8)', borderWidth: 0, textStyle: { color: '#fff', fontSize: 11 } },
			grid: { top: 8, right: 8, bottom: 24, left: 40 },
			xAxis: { type: 'category', data: months, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { fontSize: 9, color: mutedFg } },
			yAxis: { type: 'value', splitLine: { lineStyle: { color: muted, type: 'dashed' } }, axisLabel: { fontSize: 9, color: mutedFg } },
			series: [{
				type: 'bar', data: values, barWidth: '50%',
				itemStyle: { borderRadius: [3, 3, 0, 0], color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
					{ offset: 0, color: primary },
					{ offset: 1, color: withAlpha(primary, 0.6) }
				]) }
			}]
		})
		window.addEventListener('resize', () => chart.resize())
	}
}

onMounted(async () => {
	const data = await getData()
	if (data?.dbId) {
		storedData.value = data as NewSparkData
		const auth = useFaceIDAuthentication(storedData.value)
		registerFaceID = auth.registerFaceID
		removeFaceID = auth.removeFaceID
	}
	nextTick(() => initCharts())
})
</script>

<template>
	<div class="px-4 pb-4 space-y-6">
		<!-- Stats row -->
		<div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
			<div class="border rounded-md p-4 bg-background">
				<div class="flex items-center justify-between">
					<span class="text-xs font-medium uppercase tracking-wider text-muted-foreground">Uploads</span>
					<Upload class="h-4 w-4 text-muted-foreground" />
				</div>
				<p class="text-2xl font-semibold mt-2 tabular-nums">1,242</p>
				<div class="flex items-center gap-1 mt-1">
					<TrendingUp class="h-3 w-3 text-emerald-500" />
					<span class="text-xs text-emerald-500 font-medium">12.5%</span>
					<span class="text-xs text-muted-foreground">vs last period</span>
				</div>
			</div>
			<div class="border rounded-md p-4 bg-background">
				<div class="flex items-center justify-between">
					<span class="text-xs font-medium uppercase tracking-wider text-muted-foreground">Active Users</span>
					<Users class="h-4 w-4 text-muted-foreground" />
				</div>
				<p class="text-2xl font-semibold mt-2 tabular-nums">543</p>
				<div class="flex items-center gap-1 mt-1">
					<TrendingDown class="h-3 w-3 text-red-500" />
					<span class="text-xs text-red-500 font-medium">2.3%</span>
					<span class="text-xs text-muted-foreground">vs last period</span>
				</div>
			</div>
			<div class="border rounded-md p-4 bg-background">
				<div class="flex items-center justify-between">
					<span class="text-xs font-medium uppercase tracking-wider text-muted-foreground">Storage</span>
					<HardDrive class="h-4 w-4 text-muted-foreground" />
				</div>
				<div class="h-16 mt-1" ref="storageChartRef"></div>
			</div>
			<div class="border rounded-md p-4 bg-background">
				<div class="flex items-center justify-between">
					<span class="text-xs font-medium uppercase tracking-wider text-muted-foreground">This Week</span>
					<Activity class="h-4 w-4 text-muted-foreground" />
				</div>
				<p class="text-2xl font-semibold mt-2 tabular-nums">2,891</p>
				<span class="text-xs text-muted-foreground">total actions</span>
			</div>
		</div>

		<!-- Charts row -->
		<div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
			<div class="border rounded-md p-4 bg-background">
				<p class="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Activity (14 days)</p>
				<div class="h-[180px]" ref="activityChartRef"></div>
			</div>
			<div class="border rounded-md p-4 bg-background">
				<p class="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Uploads This Week</p>
				<div class="h-[180px]" ref="uploadsChartRef"></div>
			</div>
		</div>

		<Separator />

		<!-- Profile form -->
		<div class="max-w-3xl">
			<h3 class="text-sm font-semibold mb-4">Account Details</h3>
			<div class="grid md:grid-cols-2 gap-4">
				<FormField v-slot="{ componentField }" name="username">
				<FormItem>
				<FormLabel class="text-xs">Username</FormLabel>
				<FormControl>
				<Input type="text" placeholder="Username" v-model="updatedInfo.user" />
				</FormControl>
				<FormMessage />
				</FormItem>
				</FormField>

				<FormField v-slot="{ componentField }" name="email">
				<FormItem>
				<FormLabel class="text-xs">Email</FormLabel>
				<FormControl>
				<Input type="email" placeholder="Email" v-model="updatedInfo.email" />
				</FormControl>
				<FormMessage />
				</FormItem>
				</FormField>

				<FormField v-slot="{ componentField }" name="firstname">
				<FormItem>
				<FormLabel class="text-xs">First Name</FormLabel>
				<FormControl>
				<Input type="text" placeholder="First Name" v-model="updatedInfo.firstname" />
				</FormControl>
				<FormMessage />
				</FormItem>
				</FormField>

				<FormField v-slot="{ componentField }" name="lastname">
				<FormItem>
				<FormLabel class="text-xs">Last Name</FormLabel>
				<FormControl>
				<Input type="text" placeholder="Last Name" v-model="updatedInfo.lastname" />
				</FormControl>
				<FormMessage />
				</FormItem>
				</FormField>

				<div class="md:col-span-2">
					<FormField v-slot="{ componentField }" name="bio">
					<FormItem>
					<FormLabel class="text-xs">About Me</FormLabel>
					<FormControl>
					<Textarea placeholder="Tell us a little bit about yourself" v-model="updatedInfo.description" rows="3" />
					</FormControl>
					<FormMessage />
					</FormItem>
					</FormField>
				</div>
			</div>

			<div class="flex items-center gap-3 mt-4" v-if="userInfo && userInfo.id != ''">
				<FaceIDAuth :userId="parseInt(userInfo.id)" actionType="register"/>
				<span class="text-sm text-muted-foreground">{{ faceID ? 'PassKey enabled' : 'PassKey disabled' }}</span>
			</div>

			<div class="flex gap-2 mt-4">
				<Button type="submit" size="sm">Update Profile</Button>
				<Button type="button" variant="outline" size="sm" @click="resetForm">Reset</Button>
			</div>
		</div>
	</div>
</template>
